import { CosmicCostAndCoreHierarchyResponse, CosmicCostAndCoreNode } from "../models/CosmicModels";
import { useEffect, useState } from 'react';
import { CosmicService } from '../services/cosmicService';
import { useDateRange } from "./useDateSelector";
import { useCategoryFilters } from "./useFilters";
import { useCosmicFilters } from "./useCosmicFilters";
import { useSelector } from "react-redux";
import { IAppState } from "../store";
import { ServiceTreeItem } from "../models/serviceTree";

export const useCosmicCostAndCoreHierarchy = (selectedMonth: string) => {
    const [data, setData] = useState<CosmicCostAndCoreHierarchyResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const range = useDateRange();
    const filtersData = useCategoryFilters().filters;
    const cosmicSearchFilters = useCosmicFilters('defaultFilterKey').cosmicSearchFilters;
    const serviceIdMap = useSelector<IAppState, Map<string, ServiceTreeItem>>((state) => state.serviceTree.indexMap);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const response = await CosmicService.getCosmicCostAndCoreHierarchy(range.startDate, range.endDate, selectedMonth, cosmicSearchFilters || '', filtersData.filters, serviceIdMap, '');
                // set empty [] chindren for root node
                for (const item of response.data) {
                    item.children = [];
                }
                setData(response);
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err : new Error('An error occurred'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [range.startDate, range.endDate, cosmicSearchFilters, filtersData.filters, serviceIdMap, selectedMonth]);

    const query = async (path: string) => {
        const response = await CosmicService.getCosmicCostAndCoreHierarchy(range.startDate, range.endDate, selectedMonth, cosmicSearchFilters || '', filtersData.filters, serviceIdMap, path);
        if (!data || !data.data) return;
        function updateChildren(nodes: CosmicCostAndCoreNode[], targetId: string, newChildren: CosmicCostAndCoreNode[]) {
            for (const node of nodes) {
                if (node.id === targetId) {
                    node.children = newChildren;
                    return true;
                }
                if (node.children && node.children.length > 0) {
                    if (updateChildren(node.children, targetId, newChildren)) {
                        return true;
                    }
                }
            }
            return false;
        }
        updateChildren(data.data, path, response.data);
    }

    const clear = async (path: string) => {
        if (!data || !data.data) return;
        function clearChildren(nodes: CosmicCostAndCoreNode[], targetId: string) {
            for (const node of nodes) {
                if (node.id === targetId) {
                    node.children = [];
                    return true;
                }
                if (node.children && node.children.length > 0) {
                    if (clearChildren(node.children, targetId)) {
                        return true;
                    }
                }
            }
            return false;
        }
        clearChildren(data.data, path);
    }

    return { data, query, clear, isLoading, error };
};